/**
 * Discover git repositories under a working directory for bridge reporting and worktrees.
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { getBridgeRoot } from '../files/cwd/index.js';
import { yieldToEventLoop } from '../runtime/yield-to-event-loop.js';
import type { DiscoveredGitRepo } from './types.js';
import { getRemoteOriginUrl } from './remote-origin-url.js';
import { isGitRepoDirectory } from './is-git-repo.js';

const GIT_DISCOVER_YIELD_EVERY = 32;

async function yieldGitDiscoverWork(entryCount: number): Promise<void> {
  if (entryCount > 0 && entryCount % GIT_DISCOVER_YIELD_EVERY === 0) {
    await yieldToEventLoop();
  }
}

/**
 * CWD (if a git dir) and immediate child directories that are git repos.
 */
export async function discoverGitRepos(cwd: string = getBridgeRoot()): Promise<DiscoveredGitRepo[]> {
  const result: DiscoveredGitRepo[] = [];
  const cwdResolved = path.resolve(cwd);

  if (await isGitRepoDirectory(cwdResolved)) {
    const remoteUrl = await getRemoteOriginUrl(cwdResolved);
    result.push({ absolutePath: cwdResolved, remoteUrl });
  }

  let entries: fs.Dirent[];
  try {
    entries = fs.readdirSync(cwdResolved, { withFileTypes: true });
  } catch {
    return result;
  }

  for (let i = 0; i < entries.length; i++) {
    await yieldGitDiscoverWork(i + 1);
    const ent = entries[i]!;
    if (!ent.isDirectory()) continue;
    const childPath = path.join(cwdResolved, ent.name);
    if (await isGitRepoDirectory(childPath)) {
      const remoteUrl = await getRemoteOriginUrl(childPath);
      result.push({ absolutePath: childPath, remoteUrl });
    }
  }

  return result;
}

/**
 * All git repository roots under `rootPath` (depth-first). Does not descend into a directory that is already a repo root.
 */
export async function discoverGitReposUnderRoot(rootPath: string): Promise<DiscoveredGitRepo[]> {
  const root = path.resolve(rootPath);
  const roots: string[] = [];

  let walkEntries = 0;

  async function walk(dir: string): Promise<void> {
    if (await isGitRepoDirectory(dir)) {
      roots.push(path.resolve(dir));
      return;
    }
    let entries: fs.Dirent[];
    try {
      entries = fs.readdirSync(dir, { withFileTypes: true });
    } catch {
      return;
    }
    for (let i = 0; i < entries.length; i++) {
      await yieldGitDiscoverWork(++walkEntries);
      const ent = entries[i]!;
      if (!ent.isDirectory() || ent.name === '.git') continue;
      await walk(path.join(dir, ent.name));
    }
  }

  await walk(root);

  const uniq = [...new Set(roots)];
  const out: DiscoveredGitRepo[] = [];
  for (let i = 0; i < uniq.length; i++) {
    await yieldGitDiscoverWork(i + 1);
    const p = uniq[i]!;
    const remoteUrl = await getRemoteOriginUrl(p);
    out.push({ absolutePath: p, remoteUrl });
  }
  return out;
}
